import { Theme, Typography, useTheme } from "@material-ui/core";
import { IResourceListProps } from "../../../common/ra-types";
import { DefaultProLogsList } from "../../../common/list/DefaultProLogsList";
import {
  CreateButton,
  Datagrid,
  FunctionField,
  NumberField,
  Record,
  TextField,
  useListContext,
} from "react-admin";
import { Void } from "../../../common/data-grid-fields/Void";
import ProLogsDateField from "../../../common/data-grid-fields/ProLogsDateField";
import {
  EldFileImportListDto,
  ImportStatus,
} from "../../../../common/data/service-proxies";
import ColoredStatusField from "../../../common/data-grid-fields/ColoredStatusField";
import { ActionsField } from "../../../common/data-grid-fields/ActionsField";

interface ActionFieldProps extends IResourceListProps {
  record?: EldFileImportListDto & Record;
}

const ListActions = () => {
  const { basePath } = useListContext();

  return (
    <>
      <CreateButton basePath={basePath} />
    </>
  );
};

const getRegistrationStatus = (record?: EldFileImportListDto) => {
  return record?.importStatus;
};

const getStatusTextColor = (record: any, theme: Theme) => {
  switch (getRegistrationStatus(record)) {
    case ImportStatus.ImportSuccess:
    case ImportStatus.ReadyToImport:
      return theme.palette.success.contrastText;
    case ImportStatus.ImportFailed:
    case ImportStatus.ParsingFailed:
      return theme.palette.error.contrastText;
    case ImportStatus.Created:
    case ImportStatus.Importing:
    case ImportStatus.Parsing:
      return theme.palette.warning.contrastText;
    default:
      return "auto";
  }
};

const getStatusBackgroundColor = (record: any, theme: Theme) => {
  switch (getRegistrationStatus(record)) {
    case ImportStatus.ImportSuccess:
    case ImportStatus.ReadyToImport:
      return theme.palette.success.main;
    case ImportStatus.Importing:
    case ImportStatus.Parsing:
      return theme.palette.warning.main;
    case ImportStatus.Created:
      return theme.palette.info.main;
    case ImportStatus.ImportFailed:
    case ImportStatus.ParsingFailed:
      return theme.palette.error.main;

    default:
      return "auto";
  }
};

const ListItemActions = ({ record, ...props }: ActionFieldProps) => {
  return (
    <ActionsField
      {...props}
      record={record}
      editLabel="Import"
      editable={(record) => record.importStatus === ImportStatus.ReadyToImport}
    ></ActionsField>
  );
};

export const EldImportList = (props: IResourceListProps) => {
  const theme: Theme = useTheme();

  return (
    <div className="content-container">
      <DefaultProLogsList
        title="resources.admin-eld-import.name"
        bulkActionButtons={false}
        actions={<ListActions />}
        filters={<Void />}
        sort={{ field: "creationTime", order: "ASC" }}
        {...props}
      >
        <Datagrid>
          <ProLogsDateField
            showTime
            source="creationTime"
            label="resources.admin-eld-import.labels.creationTime"
          />
          <FunctionField<EldFileImportListDto & Record>
            source="creatorUserName"
            label="resources.admin-eld-import.labels.creatorUserName"
            render={(record) => (
              <div>
                <div>
                  <Typography variant="body2">
                    {record?.creatorName} {record?.creatorSurname}
                  </Typography>
                </div>
                <div>
                  <Typography variant="body2">
                    {record?.creatorUserName}
                  </Typography>
                </div>
              </div>
            )}
          />
          <TextField
            source="description"
            label="resources.admin-eld-import.labels.description"
          />
          <NumberField
            source="filesCount"
            label="resources.admin-eld-import.labels.filesCount"
          />
          <ColoredStatusField
            className="centered-content"
            headerClassName="centered-content"
            color={(record, _, __) => getStatusTextColor(record, theme)}
            backgroundColor={(record, _, __) =>
              getStatusBackgroundColor(record, theme)
            }
            label="resources.admin-eld-import.labels.status"
            applyColorTo="everything"
            labelStyle="columns"
            source="importStatus"
            labelTranslationNamespace="resources.admin-eld-import.statuses"
          />
          <ListItemActions {...props} />
        </Datagrid>
      </DefaultProLogsList>
    </div>
  );
};
